import React from "react";
import { Box, Checkbox, Text, UnstyledButton } from "@mantine/core";
import InfoTooltip from "../../../common/InfoTooltip";
import {
  ReportMetricDef,
  ReportMetricSection,
} from "../../../../types/reportsV2";
import MetricTooltip, { hasMetricHelp } from "../MetricTooltip";
import styles from "./CustomizeDrawer.module.css";

export interface MetricChecklistProps {
  sections: ReportMetricSection[];
  /** Every metric (static + this run's dynamic ones), in display order. */
  metrics: ReportMetricDef[];
  isChecked: (key: string) => boolean;
  /** Disable a metric's checkbox (unavailable, or a limit reached). */
  isDisabled: (metric: ReportMetricDef) => boolean;
  onToggle: (key: string) => void;
  /** Adds the section header's Select all / Deselect all. */
  onToggleSection?: (keys: string[], on: boolean) => void;
}

/**
 * Metrics grouped by the definition's sections, each with a checkbox and an
 * info tooltip (description + formula from the definition). Unavailable
 * metrics are disabled and their tooltip says why.
 */
export default function MetricChecklist({
  sections,
  metrics,
  isChecked,
  isDisabled,
  onToggle,
  onToggleSection,
}: MetricChecklistProps) {
  return (
    <>
      {sections.map((section) => {
        const items = metrics.filter((m) => m.section === section.key);
        if (items.length === 0) return null;
        const selectable = items.filter((m) => m.available).map((m) => m.key);
        const checkedCount = items.filter((m) => isChecked(m.key)).length;
        const allOn = selectable.every((k) => isChecked(k));
        return (
          <Box
            key={section.key}
            role="group"
            aria-label={section.label}
            className={styles.section}
          >
            <Box className={styles.sectionHeader}>
              <Text component="span" className={styles.sectionLabel}>
                {section.label}
              </Text>
              <Text component="span" className={styles.sectionCount}>
                {checkedCount}/{items.length}
              </Text>
              {onToggleSection && selectable.length > 0 && (
                <UnstyledButton
                  type="button"
                  className={styles.sectionAction}
                  onClick={() => onToggleSection(selectable, !allOn)}
                  aria-label={`${allOn ? "Deselect" : "Select"} all ${section.label}`}
                >
                  {allOn ? "Deselect all" : "Select all"}
                </UnstyledButton>
              )}
            </Box>
            {items.map((metric) => (
              <Box
                key={metric.key}
                className={styles.metricRow}
                data-disabled={isDisabled(metric) || undefined}
              >
                <Checkbox
                  label={metric.label}
                  checked={isChecked(metric.key)}
                  disabled={isDisabled(metric)}
                  onChange={() => onToggle(metric.key)}
                />
                {hasMetricHelp(metric) && (
                  <InfoTooltip
                    label={<MetricTooltip metric={metric} />}
                    ariaLabel={`About ${metric.label}`}
                    position="left"
                  />
                )}
              </Box>
            ))}
          </Box>
        );
      })}
    </>
  );
}
